import { runAfterLoad } from './gsap-lazy';

const FALLBACK_LOAD_DELAY_MS = 350;
const INTRO_DURATION_S = 1.05;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function normalizeAngle(angle) {
  let wrapped = angle;
  while (wrapped > Math.PI) wrapped -= Math.PI * 2;
  while (wrapped < -Math.PI) wrapped += Math.PI * 2;
  return wrapped;
}

function lerpAngle(a, b, t) {
  return a + normalizeAngle(b - a) * t;
}

function easeOutCubic(t) {
  const p = clamp(t, 0, 1);
  return 1 - Math.pow(1 - p, 3);
}

runAfterLoad(() => {
  const heroSection = document.getElementById('Startseite');
  const vorteileSection = document.getElementById('Vorteile');
  const faqSection = document.getElementById('FAQ');
  const modelContainer = document.getElementById('floating-model-container');
  const parallaxLayers = Array.from(heroSection?.querySelectorAll('.parallax-layer') || []);

  if (!heroSection || !modelContainer) return;

  const state = {
    modulesPromise: null,
    THREE: null,
    scene: null,
    camera: null,
    renderer: null,
    model: null,
    rig: null,
    mixer: null,
    ktx2Loader: null,
    profile: null,
    curve: null,
    introPosition: { x: 0, y: 0, z: 0 },
    spawnPosition: { x: 0, y: 0, z: 0 },
    pathRotationOffset: { x: 0, y: 0, z: 0 },
    spawnRotation: { x: 0, y: 0, z: 0 },
    currentPosition: { x: 0, y: 0, z: 0 },
    currentRotation: { x: 0, y: 0, z: 0 },
    introProgress: 0,
    isLoading: false,
    isMounted: false,
    hasLoadError: false,
    loadScheduled: false,
    isPageVisible: !document.hidden,
    rafId: 0,
    lastFrameMs: 0,
  };
  const parallaxState = {
    rafId: 0,
  };

  function setModelPhase(phase) {
    modelContainer.classList.toggle('model-idle', phase === 'idle');
    modelContainer.classList.toggle('model-loading', phase === 'loading');
    modelContainer.classList.toggle('model-ready', phase === 'ready');
  }

  function getDeviceProfile() {
    const isMobile = window.innerWidth <= 900;

    if (isMobile) {
      return {
        camera: {
          fov: 54,
          position: { x: 0, y: 0, z: 10 },
          lookAt: { x: 0, y: 0, z: 0 },
        },
        modelTargetSize: 50,
        introPosition: { x: 0, y: -3, z: 0 },
        introRotation: { x: 0, y: 0, z: 0 },
        pathRotationOffset: { x: 0, y: 0, z: 0 },
        path: [
          { x: -3, y: -5, z: 0 },
          { x: -5, y: -4, z:0 },
          { x: -1, y: -3.5, z: -0.75 },
          { x: -3, y: -1.5, z: 1.6 },
        ],
      };
    }

    return {
      camera: {
        fov: 54,
        position: { x: 0, y: 0, z: 10 },
        lookAt: { x: 0, y: 0, z: 0 },
      },
      modelTargetSize: 72,
      introPosition: { x: 0, y: -3, z: 0 },
      introRotation: { x: 0, y: 0, z: 0 },
      pathRotationOffset: { x: 0, y: 0, z: 0 },
      path: [
        { x: -3, y: -5, z: 0 },
        { x: -1, y: -6, z: -0.75 },
        { x: -4, y: -7, z: 1.6 },
        { x: -5, y: -4, z: 0 },
      ],
    };
  }

  function getScrollProgress() {
    const heroTop = heroSection.offsetTop;
    const start = heroTop + heroSection.offsetHeight * 0.4;
    const endAnchor = faqSection || vorteileSection;
    const end = (endAnchor ? endAnchor.offsetTop : heroTop + window.innerHeight * 1.8) - window.innerHeight * 0.35;

    if (end <= start) return 0;
    return clamp((window.scrollY - start) / (end - start), 0, 1);
  }

  function applyHeroParallax() {
    const rect = heroSection.getBoundingClientRect();
    const progress = clamp(-rect.top / Math.max(rect.height, 1), 0, 1);

    parallaxLayers.forEach((layer) => {
      const depth = parseFloat(layer.dataset.depth) || 0;
      layer.style.setProperty('--y', `${progress * depth * 800}px`);
    });
  }

  function requestParallaxUpdate() {
    if (parallaxState.rafId) return;

    parallaxState.rafId = requestAnimationFrame(() => {
      parallaxState.rafId = 0;
      applyHeroParallax();
    });
  }

  function updateCurve() {
    if (!state.THREE) return;

    state.profile = getDeviceProfile();
    state.introPosition = { ...state.profile.introPosition };
    state.spawnPosition = { ...state.profile.introPosition };
    state.spawnRotation = { ...state.profile.introRotation };
    state.pathRotationOffset = { ...state.profile.pathRotationOffset };
    state.curve = new state.THREE.CatmullRomCurve3(
      state.profile.path.map((point) => new state.THREE.Vector3(point.x, point.y, point.z)),
      false,
      'centripetal'
    );
  }

  function getPathTarget(timeS, progress = getScrollProgress()) {
    if (!state.curve) {
      return {
        position: { ...state.spawnPosition },
        rotation: { ...state.spawnRotation },
      };
    }

    const point = state.curve.getPoint(progress);
    const lookOffset = 0.015;
    const aheadPoint = state.curve.getPoint(clamp(progress + lookOffset, 0, 1));
    const behindPoint = state.curve.getPoint(clamp(progress - lookOffset, 0, 1));
    const tangent = aheadPoint.clone().sub(behindPoint);
    if (tangent.lengthSq() < 0.000001) {
      tangent.set(0, 0, -1);
    } else {
      tangent.normalize();
    }
    const yaw = Math.atan2(tangent.x, tangent.z);
    const pitch = -Math.atan2(tangent.y, Math.max(Math.hypot(tangent.x, tangent.z), 0.0001));

    const driftX = Math.sin(timeS * 0.92 + progress * 6.8) * 0.2;
    const driftY = Math.cos(timeS * 0.86 + progress * 5.6) * 0.12;
    const driftZ = Math.sin(timeS * 0.72 + progress * 4.6) * 0.01;

    const pitchMotion = Math.cos(timeS * 1.1 + progress * 6.1) * 0.08;
    const yawMotion = Math.sin(timeS * 0.94 + progress * 5.9) * 0.1;
    const rollMotion = Math.sin(timeS * 1.3 + progress * 8.1) * 0.15;
    const progressSpinY = progress * Math.PI * 0.7;
    const progressSpinZ = progress * Math.PI * 0.1;

    return {
      position: {
        x: point.x + driftX,
        y: point.y + driftY,
        z: point.z + driftZ,
      },
      rotation: {
        x: state.pathRotationOffset.x + pitch * 0.3 + pitchMotion,
        y: state.pathRotationOffset.y + yaw * 0.15 + yawMotion + progressSpinY,
        z: state.pathRotationOffset.z + rollMotion + progressSpinZ,
      },
    };
  }

  function normalizeModel() {
    if (!state.model || !state.THREE || !state.profile) return;

    const box = new state.THREE.Box3().setFromObject(state.model);
    const size = new state.THREE.Vector3();
    const center = new state.THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    state.model.position.sub(center);
    const maxDimension = Math.max(size.x, size.y, size.z, 2);
    const scale = state.profile.modelTargetSize / maxDimension;
    state.model.scale.setScalar(scale);
  }

  async function loadThreeModules() {
    if (!state.modulesPromise) {
      state.modulesPromise = Promise.all([
        import('three'),
        import('three/examples/jsm/loaders/GLTFLoader.js'),
        import('three/examples/jsm/loaders/KTX2Loader.js'),
        import('three/examples/jsm/libs/meshopt_decoder.module.js'),
      ]).then(([THREE, { GLTFLoader }, { KTX2Loader }, { MeshoptDecoder }]) => ({
        THREE,
        GLTFLoader,
        KTX2Loader,
        MeshoptDecoder,
      }));
    }
    return state.modulesPromise;
  }

  function resizeRenderer() {
    if (!state.renderer || !state.camera) return;

    updateCurve();

    const width = window.innerWidth;
    const height = window.innerHeight;
    const pixelRatioCap = width <= 900 ? 1.25 : 1.5;

    state.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, pixelRatioCap));
    state.renderer.setSize(width, height);
    state.camera.aspect = width / Math.max(height, 1);
    state.camera.fov = state.profile.camera.fov;
    state.camera.position.set(
      state.profile.camera.position.x,
      state.profile.camera.position.y,
      state.profile.camera.position.z
    );
    state.camera.updateProjectionMatrix();
    state.camera.lookAt(
      state.profile.camera.lookAt.x,
      state.profile.camera.lookAt.y,
      state.profile.camera.lookAt.z
    );

    normalizeModel();
  }

  function scheduleBembelLoad() {
    if (state.loadScheduled || state.isLoading || state.isMounted || state.hasLoadError) return;
    state.loadScheduled = true;
    setModelPhase('loading');

    const runTask = () => {
      void mountModel();
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(runTask, { timeout: 150 });
      return;
    }

    window.setTimeout(runTask, 10);
  }

  function startLoop() {
    if (state.rafId) return;

    const animate = (nowMs) => {
      state.rafId = requestAnimationFrame(animate);

      if (!state.renderer || !state.scene || !state.camera || !state.rig || !state.isPageVisible) return;

      if (!state.lastFrameMs) state.lastFrameMs = nowMs;
      const delta = Math.min((nowMs - state.lastFrameMs) / 1000, 0.05);
      state.lastFrameMs = nowMs;

      const timeS = nowMs / 1000;
      const pathTarget = getPathTarget(timeS, getScrollProgress());

      state.introProgress = Math.min(1, state.introProgress + delta / INTRO_DURATION_S);
      const introBlend = easeOutCubic(state.introProgress);

      const desiredPosition = {
        x: lerp(state.spawnPosition.x, pathTarget.position.x, introBlend),
        y: lerp(state.spawnPosition.y, pathTarget.position.y, introBlend),
        z: lerp(state.spawnPosition.z, pathTarget.position.z, introBlend),
      };
      const desiredRotation = {
        x: lerp(state.spawnRotation.x, pathTarget.rotation.x, introBlend),
        y: lerp(state.spawnRotation.y, pathTarget.rotation.y, introBlend),
        z: lerp(state.spawnRotation.z, pathTarget.rotation.z, introBlend),
      };

      const positionSmoothing = 1 - Math.exp(-delta * 1.65);
      const rotationSmoothing = 1 - Math.exp(-delta * 1.25);

      state.currentPosition.x = lerp(state.currentPosition.x, desiredPosition.x, positionSmoothing);
      state.currentPosition.y = lerp(state.currentPosition.y, desiredPosition.y, positionSmoothing);
      state.currentPosition.z = lerp(state.currentPosition.z, desiredPosition.z, positionSmoothing);
      state.currentRotation.x = lerpAngle(state.currentRotation.x, desiredRotation.x, rotationSmoothing);
      state.currentRotation.y = lerpAngle(state.currentRotation.y, desiredRotation.y, rotationSmoothing);
      state.currentRotation.z = lerpAngle(state.currentRotation.z, desiredRotation.z, rotationSmoothing);

      state.rig.position.set(state.currentPosition.x, state.currentPosition.y, state.currentPosition.z);
      state.rig.rotation.set(state.currentRotation.x, state.currentRotation.y, state.currentRotation.z);

      if (state.mixer) state.mixer.update(delta);
      state.renderer.render(state.scene, state.camera);
    };

    state.rafId = requestAnimationFrame(animate);
  }

  async function mountModel() {
    if (state.isLoading || state.isMounted || state.hasLoadError) return;
    state.isLoading = true;

    try {
      const { THREE, GLTFLoader, KTX2Loader, MeshoptDecoder } = await loadThreeModules();
      state.THREE = THREE;
      updateCurve();

      state.scene = new THREE.Scene();
      state.camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 10000);

      state.renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
      });
      state.renderer.setClearAlpha(0);
      state.renderer.shadowMap.enabled = false;
      state.renderer.shadowMap.type = THREE.PCFShadowMap;
      if ('outputColorSpace' in state.renderer) {
        state.renderer.outputColorSpace = THREE.SRGBColorSpace;
      }
      state.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      state.renderer.toneMappingExposure = 1.16;

      if (!modelContainer.contains(state.renderer.domElement)) {
        modelContainer.appendChild(state.renderer.domElement);
      }

      const keyLight = new THREE.DirectionalLight(0xfff6ea, 4.6);
      keyLight.position.set(90, 135, 110);
      state.scene.add(keyLight);
      const fillLight = new THREE.HemisphereLight(0xc9eeff, 0x2f1f35, 1.15);
      state.scene.add(fillLight);
      const rimLight = new THREE.DirectionalLight(0x8ac3ff, 2.35);
      rimLight.position.set(-120, 85, -100);
      state.scene.add(rimLight);
      const ambient = new THREE.AmbientLight(0xffffff, 0.24);
      state.scene.add(ambient);

      const loader = new GLTFLoader();
      const transcoderPath = `${import.meta.env.BASE_URL}basis/`;
      state.ktx2Loader = new KTX2Loader().setTranscoderPath(transcoderPath).detectSupport(state.renderer);
      loader.setKTX2Loader(state.ktx2Loader);
      loader.setMeshoptDecoder(MeshoptDecoder);

      const bembelUrl = new URL('bembel.glb', import.meta.url);
      loader.load(
        bembelUrl.href,
        (gltf) => {
          state.model = gltf.scene;
          state.rig = new THREE.Group();
          state.rig.add(state.model);
          state.scene.add(state.rig);

          normalizeModel();

          if (gltf.animations.length > 0) {
            state.mixer = new THREE.AnimationMixer(state.model);
            gltf.animations.forEach((clip) => state.mixer.clipAction(clip).play());
          }

          const initialProgress = getScrollProgress();
          state.introProgress = Math.min(0.82, initialProgress * 1.2);
          state.currentPosition = { ...state.spawnPosition };
          state.currentRotation = { ...state.spawnRotation };

          if (state.introProgress > 0.6) {
            const lateTarget = getPathTarget(performance.now() / 1000, initialProgress);
            const blend = clamp((state.introProgress - 0.6) / 0.4, 0, 1) * 0.8;
            state.currentPosition = {
              x: lerp(state.spawnPosition.x, lateTarget.position.x, blend),
              y: lerp(state.spawnPosition.y, lateTarget.position.y, blend),
              z: lerp(state.spawnPosition.z, lateTarget.position.z, blend),
            };
            state.currentRotation = {
              x: lerp(state.spawnRotation.x, lateTarget.rotation.x, blend),
              y: lerp(state.spawnRotation.y, lateTarget.rotation.y, blend),
              z: lerp(state.spawnRotation.z, lateTarget.rotation.z, blend),
            };
          }

          resizeRenderer();
          setModelPhase('ready');
          state.isLoading = false;
          state.isMounted = true;
          startLoop();
        },
        undefined,
        (error) => {
          if (!state.hasLoadError) {
            console.error('Error loading bembel model:', error);
            state.hasLoadError = true;
          }
          state.isLoading = false;
          setModelPhase('idle');
        }
      );

      resizeRenderer();
    } catch (error) {
      if (!state.hasLoadError) {
        console.error('Error initializing bembel scene:', error);
        state.hasLoadError = true;
      }
      state.isLoading = false;
      setModelPhase('idle');
    }
  }

  function onVisibilityChange() {
    state.isPageVisible = !document.hidden;
    if (!state.isPageVisible) return;
    state.lastFrameMs = performance.now();
  }

  setModelPhase('idle');
  requestParallaxUpdate();

  window.addEventListener('scroll', requestParallaxUpdate, { passive: true });
  window.addEventListener('resize', () => {
    resizeRenderer();
    requestParallaxUpdate();
  });
  document.addEventListener('visibilitychange', onVisibilityChange);

  const triggerLoad = () => scheduleBembelLoad();
  window.addEventListener('pointerdown', triggerLoad, { once: true, passive: true });
  window.addEventListener('pointermove', triggerLoad, { once: true, passive: true });
  window.addEventListener('wheel', triggerLoad, { once: true, passive: true });
  window.addEventListener('touchstart', triggerLoad, { once: true, passive: true });
  window.addEventListener('scroll', triggerLoad, { once: true, passive: true });
  window.addEventListener('keydown', triggerLoad, { once: true });
  window.setTimeout(scheduleBembelLoad, FALLBACK_LOAD_DELAY_MS);
});
